import { useEffect, useState } from 'react';
import {
  businessToday,
  handoverDayLabel,
  shortWindow,
  HANDOVER_DAY_RULE_LABEL,
  HANDOVER_DAY_RULES,
  type HandoverDay,
} from '@bbbk/schema';
import { Icon, useToast, writeWithToast } from '../common';
import { api, ApiError } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { errMsg } from './utils';
import {
  EMPTY_DRAFT,
  WEEKDAYS,
  checkDraft,
  upcoming,
  weeklyRows,
  withWeekly,
  withoutWeekly,
  type Draft,
  type DraftError,
  type WeekdayKey,
  type WeeklyRow,
} from './handoverDays';

// Account › Customer portal › Restricted drop-off and collection days
// (handover-days; docs/design/handover-days.html, flow 1.1).
//
// Freddi, 2026-10-12: "they should be added as cards to the customer portal so
// we can easily add/remove dates like this as we want". One list, a row per
// date, run of dates or weekday, each Afternoon only or Closed; an add row
// beneath that saves on Add. The dated rows are /api/handover-days; the weekly
// ones are the setting `handover_closed_weekly`, saved through PATCH /settings
// like every other portal setting. The customer side reads both and is not
// changed here — the calendar already draws both states.

const RULE_CLASS = { pm_only: 'pm', closed: 'closed', am_only: 'pm' } as const;
const RULE_TEXT = { ...HANDOVER_DAY_RULE_LABEL, am_only: 'Morning only' } as const;

export interface HandoverDaysCardProps {
  phase: 'ready' | 'error';
  weekly: WeeklyRow[];
  rows: HandoverDay[];
  draft: Draft;
  error: DraftError | null;
  busy: boolean;
  pmWindow: string;
  onDraft: (patch: Partial<Draft>) => void;
  onAdd: () => void;
  onRemoveRow: (row: HandoverDay) => void;
  onRemoveWeekly: (row: WeeklyRow) => void;
  onRetry: () => void;
}

const days = (r: HandoverDay) => Math.round((Date.parse(`${r.endsOn}T12:00:00Z`) - Date.parse(`${r.startsOn}T12:00:00Z`)) / 86_400_000) + 1;

/** The card, drawn from its props — the visual contract (handover-days-card.test.tsx). */
export function HandoverDaysCard(p: HandoverDaysCardProps) {
  const head = (
    <div className="fin-panel-head">
      <h3>Restricted drop-off and collection days</h3>
      <span className="panel-meta">Days customers can't book a morning, or any time</span>
    </div>
  );
  if (p.phase === 'error') {
    // A FAILED READ IS NOT AN EMPTY LIST (the Dated overrides lesson): "no days
    // set" here would be a claim about the kennel made on a request that never
    // answered.
    return (
      <section className="fin-panel">
        {head}
        <div className="account-error">
          <Icon name="alert" />
          Could not load the restricted drop-off and collection days. Customers may be seeing days this card would close.
          <button type="button" className="btn ghost sm" onClick={p.onRetry}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  const bad = (f: DraftError['field']) => (p.error?.field === f ? 'field bad' : 'field');
  const d = p.draft;
  const any = p.weekly.length > 0 || p.rows.length > 0;
  return (
    <section className="fin-panel">
      {head}
      <p className="pf-note">
        For customers only. The desk can still book any day. Dogs already in stay over every one of these days; only the
        handover moves. <strong>Afternoon only</strong> means {shortWindow(p.pmWindow)}. Drop-offs and collections follow the
        same rule.
      </p>

      {any ? (
        <ul className="hd-list">
          <li className="hd-row head" aria-hidden="true">
            <span>When</span>
            <span>Rule</span>
            <span>Reason</span>
            <span />
          </li>
          {p.weekly.map((w) => (
            <li className="hd-row weekly" key={w.key}>
              <span className="hd-when">
                Every {w.day}
                <small>every week</small>
              </span>
              <span className={'hd-rule ' + RULE_CLASS[w.rule]}>{RULE_TEXT[w.rule]}</span>
              <span className="hd-note none">—</span>
              <button type="button" className="btn ghost sm" aria-label={`Remove every ${w.day}`} onClick={() => p.onRemoveWeekly(w)} disabled={p.busy}>
                <Icon name="close" />
              </button>
            </li>
          ))}
          {p.rows.map((r) => {
            const label = handoverDayLabel(r);
            const n = days(r);
            return (
              <li className="hd-row" key={r.id}>
                <span className="hd-when">
                  {label}
                  {n > 1 && <small>{n} days</small>}
                </span>
                <span className={'hd-rule ' + RULE_CLASS[r.rule]}>{RULE_TEXT[r.rule]}</span>
                {/* ALWAYS RENDERED, a dash when empty — a cell that mounts
                    conditionally is a grid column that disappears. */}
                <span className={'hd-note' + (r.note ? '' : ' none')}>{r.note ?? '—'}</span>
                <button type="button" className="btn ghost sm" aria-label={`Remove ${label}`} onClick={() => p.onRemoveRow(r)} disabled={p.busy}>
                  <Icon name="close" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="pf-note pf-empty">No days set. Customers can drop off and collect at either time, every day.</p>
      )}

      <div className="hd-add">
        <div className="field hd-fit">
          <span className="field-label">When</span>
          <div className="hd-seg" role="group" aria-label="Dates or every week">
            {(['dates', 'weekly'] as const).map((m) => (
              <button type="button" key={m} className={d.mode === m ? 'on' : ''} aria-pressed={d.mode === m} onClick={() => p.onDraft({ mode: m })}>
                {m === 'dates' ? 'Dates' : 'Every week'}
              </button>
            ))}
          </div>
        </div>
        {d.mode === 'dates' ? (
          <>
            <label className={bad('from')}>
              <span className="field-label">From</span>
              <input type="date" value={d.from} aria-label="First day" onChange={(e) => p.onDraft({ from: e.target.value })} />
            </label>
            <label className={bad('to')}>
              <span className="field-label">To</span>
              <input type="date" value={d.to} min={d.from || undefined} aria-label="Last day — leave blank for one day" onChange={(e) => p.onDraft({ to: e.target.value })} />
            </label>
          </>
        ) : (
          <label className={bad('weekday')}>
            <span className="field-label">Every</span>
            <select value={d.weekday} aria-label="Weekday" onChange={(e) => p.onDraft({ weekday: e.target.value as WeekdayKey })}>
              {WEEKDAYS.map(([key, day]) => (
                <option key={key} value={key}>
                  {day}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="field hd-fit">
          <span className="field-label">Rule</span>
          <div className="hd-seg" role="radiogroup" aria-label="Rule">
            {HANDOVER_DAY_RULES.map((r) => (
              <button type="button" key={r} role="radio" aria-checked={d.rule === r} className={d.rule === r ? 'on' : ''} onClick={() => p.onDraft({ rule: r })}>
                {HANDOVER_DAY_RULE_LABEL[r]}
              </button>
            ))}
          </div>
        </div>
        {/* A weekly rule is kept as weekday and rule only, so it asks for no reason (PR #256 review). */}
        {d.mode !== 'weekly' && (
          <label className="field hd-reason">
            <span className="field-label">Reason</span>
            <input type="text" maxLength={120} placeholder="Bank holiday, Christmas…" value={d.note} aria-label="Why — optional" onChange={(e) => p.onDraft({ note: e.target.value })} />
          </label>
        )}
        <button type="button" className="btn primary" disabled={p.busy} onClick={p.onAdd}>
          Add
        </button>
        {p.error && (
          <p className="hd-err" role="alert">
            {p.error.message}
          </p>
        )}
      </div>
    </section>
  );
}

/** The card with its data: the rows from /api/handover-days, the weekly rule from the portal settings. */
export function HandoverDaysPanel({ closedWeekly, pmWindow, onWeeklySaved }: { closedWeekly: string[]; pmWindow: string; onWeeklySaved: () => void }) {
  const toast = useToast();
  const res = useResource('portal:handover-days', () => api<HandoverDay[]>('/handover-days').then((r) => r.data), { invalidatedBy: ['settings'] });
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<DraftError | null>(null);
  const [busy, setBusy] = useState(false);
  // THE WEEKLY RULE AS LAST SAVED. The settings read it comes from is cached
  // for minutes and a reload does not bypass that cache, so after a save the
  // prop can come back unchanged — and the row she just removed would stay.
  // What the PATCH wrote is the truth until the settings themselves move on.
  const [savedWeekly, setSavedWeekly] = useState<string[] | null>(null);
  const weeklyKey = JSON.stringify(closedWeekly);
  useEffect(() => setSavedWeekly(null), [weeklyKey]);
  const weeklyNow = savedWeekly ?? closedWeekly;

  if (res.state.phase === 'loading') {
    return (
      <section className="fin-panel" aria-busy="true">
        <div className="fin-panel-head">
          <h3>Restricted drop-off and collection days</h3>
        </div>
      </section>
    );
  }

  const today = businessToday();
  const all = res.state.data ?? [];
  const rows = upcoming(all, today);
  const weekly = weeklyRows(weeklyNow);

  const saveWeekly = (next: string[], success: string, onDone?: () => void) =>
    writeWithToast(toast, {
      write: () => api('/settings', { method: 'PATCH', body: { handover_closed_weekly: JSON.stringify(next) } }),
      success,
      error: (e) => errMsg(e, 'Could not save that rule.'),
      onSaved: () => {
        setSavedWeekly(next);
        onWeeklySaved();
        onDone?.();
      },
    });

  const add = () => {
    const refused = checkDraft(draft, all, weeklyNow, today);
    setError(refused);
    if (refused) return;
    setBusy(true);
    const rule = HANDOVER_DAY_RULE_LABEL[draft.rule].toLowerCase();
    let saved = false;
    const done =
      draft.mode === 'weekly'
        ? saveWeekly(withWeekly(weeklyNow, draft.weekday, draft.rule), `Every ${WEEKDAYS.find(([k]) => k === draft.weekday)![1]} · ${rule} — customers see it now`, () => {
            // Only a save that landed clears the draft — writeWithToast resolves on failure too (PR #256 review).
            saved = true;
          })
        : (async () => {
            try {
              await api('/handover-days', { method: 'POST', body: { startsOn: draft.from, endsOn: draft.to || draft.from, rule: draft.rule, note: draft.note } });
              saved = true;
              toast.success(`${handoverDayLabel({ startsOn: draft.from, endsOn: draft.to || draft.from })} · ${rule} — customers see it now`);
              res.reload();
            } catch (e) {
              // The server's refusal is the card's own sentence (@bbbk/schema),
              // so it goes where the card's would — under the add row, not a toast.
              if (e instanceof ApiError && (e.status === 400 || e.status === 409)) {
                setError({ field: e.field === 'endsOn' ? 'to' : 'from', message: e.message });
              } else {
                toast.error(errMsg(e, 'Could not add those days.'));
              }
            }
          })();
    void done.finally(() => {
      setBusy(false);
      if (saved) setDraft((d) => ({ ...EMPTY_DRAFT, mode: d.mode, weekday: d.weekday }));
    });
  };

  const removeRow = (r: HandoverDay) => {
    setBusy(true);
    void writeWithToast(toast, {
      write: () => api(`/handover-days/${r.id}`, { method: 'DELETE' }),
      success: `Removed ${handoverDayLabel(r)}`,
      error: (e) => errMsg(e, 'Could not remove that row.'),
      onSaved: () => res.reload(),
    }).finally(() => setBusy(false));
  };

  const removeWeekly = (w: WeeklyRow) => {
    setBusy(true);
    void saveWeekly(withoutWeekly(weeklyNow, w.key), `Removed every ${w.day}`).finally(() => setBusy(false));
  };

  return (
    <HandoverDaysCard
      phase={res.state.phase === 'error' ? 'error' : 'ready'}
      weekly={weekly}
      rows={rows}
      draft={draft}
      error={error}
      busy={busy}
      pmWindow={pmWindow}
      onDraft={(patch) => {
        setDraft((d) => ({ ...d, ...patch }));
        setError(null);
      }}
      onAdd={add}
      onRemoveRow={removeRow}
      onRemoveWeekly={removeWeekly}
      onRetry={() => res.reload()}
    />
  );
}
